import React, { useState, useEffect } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { Chart } from "chart.js";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { firestore as db } from "firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useMaterialUIController } from "context";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { jsPDF } from "jspdf";
import logoBranca from "assets/images/logos/LogoSemFundo.png";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

// Registro global do plugin para exibir o valor no centro do Doughnut
// Registro global do plugin para exibir o valor no centro apenas em Doughnut
Chart.register({
  id: "doughnutLabel",
  beforeDatasetsDraw: (chart, args, pluginOptions) => {
    // << roda só em doughnut >>
    if (!chart || chart.config?.type !== "doughnut") return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || !meta.data.length) return;

    const total = (chart.data?.datasets?.[0]?.data || []).reduce((acc, val) => acc + (Number(val) || 0), 0) || 0;

    const { ctx } = chart;
    ctx.save();
    ctx.font = `bold ${Math.min(20, chart.width / 10)}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = pluginOptions?.color || "#000";

    const centerX = meta.data[0].x;
    const centerY = meta.data[0].y;
    ctx.fillText(formatCurrency(total), centerX, centerY);
    ctx.restore();
  },
});

// Lista de cores únicas para os gráficos
const uniqueColors = [
  "#3f51b5",
  "#4caf50",
  "#ff9800",
  "#e91e63",
  "#9c27b0",
  "#00bcd4",
  "#f44336",
  "#ff5722",
  "#795548",
  "#2196f3",
  "#8bc34a",
  "#ffc107",
  "#673ab7",
  "#03a9f4",
  "#9e9e9e",
  "#607d8b",
  "#cddc39",
  "#ffeb3b",
  "#d81b60",
  "#4db6ac",
  "#ffca28",
  "#ad1457",
  "#26a69a",
  "#ffeb3b",
  "#6d4c41",
];

// Função para parsear valores monetários de diferentes formatos
const parseCurrencyValue = (valueString) => {
  if (!valueString) return 0;

  if (typeof valueString === "number") return valueString;

  let cleanedValue = valueString.toString().replace(/[^0-9,.]/g, "");

  if (cleanedValue.includes(",") && cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(/\./g, "");
    cleanedValue = cleanedValue.replace(",", ".");
  } else if (cleanedValue.includes(",") && !cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(",", ".");
  } else if (cleanedValue.includes(".") && !cleanedValue.includes(",")) {
  } else if (cleanedValue.includes(",")) {
    const parts = cleanedValue.split(",");
    const lastPart = parts.pop();
    const integerPart = parts.join("");
    cleanedValue = `${integerPart}.${lastPart}`;
  }

  return parseFloat(cleanedValue) || 0;
};

// Função para formatar valores monetários
const formatCurrency = (value, currencyParam = "BRL") => {
  const numericValue = typeof value === "string" ? parseCurrencyValue(value) : value;

  let formattedValue;
  if (currencyParam === "BRL") {
    formattedValue = numericValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `R$${formattedValue}`;
  } else if (currencyParam === "USD") {
    formattedValue = numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `$ ${formattedValue}`;
  } else if (currencyParam === "EUR") {
    formattedValue = numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `€ ${formattedValue}`;
  } else {
    formattedValue = numericValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `R$${formattedValue}`;
  }
};

// Abrevia valores para telas pequenas: 1k, 2.5M, 1B, etc.
const abbreviateNumber = (num) => {
  const n = Number(num) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e9) return sign + (abs / 1e9).toFixed(abs % 1e9 === 0 ? 0 : 1) + "B";
  if (abs >= 1e6) return sign + (abs / 1e6).toFixed(abs % 1e6 === 0 ? 0 : 1) + "M";
  if (abs >= 1e3) return sign + (abs / 1e3).toFixed(abs % 1e3 === 0 ? 0 : 1) + "k";
  return sign + abs.toString();
};

// Abrevia nomes de meses PT-BR
const abbrevMonthPt = (full) => {
  const map = {
    Janeiro: "Jan",
    Fevereiro: "Fev",
    Março: "Mar",
    Abril: "Abr",
    Maio: "Mai",
    Junho: "Jun",
    Julho: "Jul",
    Agosto: "Ago",
    Setembro: "Set",
    Outubro: "Out",
    Novembro: "Nov",
    Dezembro: "Dez",
  };
  return map[full] || full;
};

function Relatorios() {
  const [tabIndex, setTabIndex] = useState(0);
  const [receitasYear, setReceitasYear] = useState(new Date().getFullYear());
  const [receitasMonth, setReceitasMonth] = useState(new Date().getMonth() + 1);
  const [despesasYear, setDespesasYear] = useState(new Date().getFullYear());
  const [despesasMonth, setDespesasMonth] = useState(new Date().getMonth() + 1);
  const [filterType, setFilterType] = useState("Mensal");
  const [monthlyData, setMonthlyData] = useState([]);
  const [annualData, setAnnualData] = useState(null);
  const [receitasData, setReceitasData] = useState([]);
  const [despesasData, setDespesasData] = useState([]);
  const [despesasCartao, setDespesasCartao] = useState([]);
  const [controller] = useMaterialUIController();
  const { darkMode, currency } = controller;
  const [despesasFixasData, setDespesasFixasData] = useState([]);
  const [despesasVariaveisData, setDespesasVariaveisData] = useState([]);
  const [resultadosAnuais, setResultadosAnuais] = useState({
    saldo: 0,
    totalReceitas: 0,
    totalDespesasFixas: 0,
    totalDespesasVariaveis: 0,
  });
  const [userId, setUserId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const handleTabChange = (event, newValue) => setTabIndex(newValue);
  const handleReceitasYearChange = (event) => setReceitasYear(event.target.value);
  const handleReceitasMonthChange = (event) => setReceitasMonth(event.target.value);
  const handleDespesasYearChange = (event) => setDespesasYear(event.target.value);
  const handleDespesasMonthChange = (event) => setDespesasMonth(event.target.value);
  const handleFilterChange = (event) => setFilterType(event.target.value);
  const handleStartDateChange = (event) => setStartDate(event.target.value);
  const handleEndDateChange = (event) => setEndDate(event.target.value);

  const handleMonthChange = (tab, direction) => {
    const setMonth = tab === 1 ? setReceitasMonth : setDespesasMonth;
    const setYear = tab === 1 ? setReceitasYear : setDespesasYear;
    setMonth((prev) => {
      let newMonth = prev + direction;
      let newYear = tab === 1 ? receitasYear : despesasYear;
      if (newMonth < 1) {
        newMonth = 12;
        newYear--;
      }
      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      }
      setYear(newYear);
      return newMonth;
    });
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        console.error("Usuário não autenticado.");
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userId && tabIndex === 0) {
      fetchData(null, new Date().getFullYear(), null);
    } else if (userId && tabIndex === 1) {
      fetchData("Receitas", receitasYear, receitasMonth);
    } else if (userId && tabIndex === 2) {
      fetchData("Despesas", despesasYear, despesasMonth);
    }
  }, [userId, tabIndex, filterType, receitasYear, receitasMonth, despesasYear, despesasMonth]);

  const fetchCategorias = async () => {
    try {
      const categoriasRef = collection(db, "usuarios", userId, "categorias");
      const snapshot = await getDocs(categoriasRef);
      const categoriasMap = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        categoriasMap[data.nome] = data.emoji || "🏷️";
      });
      return categoriasMap;
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
      return {};
    }
  };

  const fetchData = async (type, year, month, start, end) => {
    try {
      const categoriasMap = await fetchCategorias();
      const receitas = {};
      const despesasFixas = {};
      const despesasVariaveis = {};
      const despesasCartaoTemp = [];
      const isVisaoGeral = tabIndex === 0;

      let yearlyData = {};
      let monthly =
        isVisaoGeral && filterType === "Mensal"
          ? Array(12)
              .fill()
              .map(() => ({
                receitas: 0,
                despesasFixas: 0,
                despesasVariaveis: 0,
              }))
          : null;
      let totalReceitas = 0;
      let totalDespesasFixas = 0;
      let totalDespesasVariaveis = 0;

      const q = query(collection(db, `usuarios/${userId}/lancamentos`), where("pago", "==", true));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const [day, dataMonth, dataYear] = data.data.split("/").map(Number);
        const valor = parseCurrencyValue(data.valor);
        const categoria = data.categoria;
        const monthIndex = dataMonth - 1;

        if (!day || !dataMonth || !dataYear) return;

        const startTimestamp = start ? new Date(start).getTime() : null;
        const endTimestamp = end ? new Date(end).getTime() : null;
        const transactionDate = new Date(dataYear, dataMonth - 1, day).getTime();

        if ((!start || transactionDate >= startTimestamp) && (!end || transactionDate <= endTimestamp)) {
          if (
            (!isVisaoGeral && type === "Receitas" && dataYear === year && dataMonth === month) ||
            (!isVisaoGeral && type === "Despesas" && dataYear === year && dataMonth === month) ||
            isVisaoGeral
          ) {
            if (data.tipo === "Receita") {
              if (!receitas[categoria]) receitas[categoria] = { valor: 0, emoji: categoriasMap[categoria] || "🏷️" };
              receitas[categoria].valor += valor;
              totalReceitas += valor;
              if (isVisaoGeral && filterType === "Mensal" && dataYear === new Date().getFullYear()) {
                monthly[monthIndex].receitas += valor;
              }
              if (!yearlyData[dataYear]) yearlyData[dataYear] = { receitas: 0, despesasFixas: 0, despesasVariaveis: 0 };
              yearlyData[dataYear].receitas += valor;
            } else if (data.tipo === "Despesa Fixa") {
              if (!despesasFixas[categoria])
                despesasFixas[categoria] = { valor: 0, emoji: categoriasMap[categoria] || "🏷️" };
              despesasFixas[categoria].valor += valor;
              totalDespesasFixas += valor;
              if (isVisaoGeral && filterType === "Mensal" && dataYear === new Date().getFullYear()) {
                monthly[monthIndex].despesasFixas += valor;
              }
              if (!yearlyData[dataYear]) yearlyData[dataYear] = { receitas: 0, despesasFixas: 0, despesasVariaveis: 0 };
              yearlyData[dataYear].despesasFixas += valor;
            } else if (data.tipo === "Despesa Variável") {
              if (!despesasVariaveis[categoria])
                despesasVariaveis[categoria] = { valor: 0, emoji: categoriasMap[categoria] || "🏷️" };
              despesasVariaveis[categoria].valor += valor;
              totalDespesasVariaveis += valor;
              if (isVisaoGeral && filterType === "Mensal" && dataYear === new Date().getFullYear()) {
                monthly[monthIndex].despesasVariaveis += valor;
              }
              if (!yearlyData[dataYear]) yearlyData[dataYear] = { receitas: 0, despesasFixas: 0, despesasVariaveis: 0 };
              yearlyData[dataYear].despesasVariaveis += valor;
            }

            if (
              data.formaPagamento === "Cartão de Crédito" &&
              !isVisaoGeral &&
              type === "Despesas" &&
              dataYear === year &&
              dataMonth === month
            ) {
              despesasCartaoTemp.push({
                categoria,
                valor,
                emoji: categoriasMap[categoria] || "🏷️",
              });
            }
          }
        }
      });

      setReceitasData(
        Object.keys(receitas).map((categoria) => ({
          categoria,
          valor: receitas[categoria]?.valor || 0,
          emoji: receitas[categoria]?.emoji || "🏷️",
          porcentagem: totalReceitas ? ((receitas[categoria]?.valor / totalReceitas) * 100).toFixed(2) : "0.00",
        }))
      );

      setDespesasFixasData(
        Object.keys(despesasFixas).map((categoria) => ({
          categoria,
          valor: despesasFixas[categoria]?.valor || 0,
          emoji: despesasFixas[categoria]?.emoji || "🏷️",
          porcentagem: totalDespesasFixas
            ? ((despesasFixas[categoria]?.valor / totalDespesasFixas) * 100).toFixed(2)
            : "0.00",
        }))
      );

      setDespesasVariaveisData(
        Object.keys(despesasVariaveis).map((categoria) => ({
          categoria,
          valor: despesasVariaveis[categoria]?.valor || 0,
          emoji: despesasVariaveis[categoria]?.emoji || "🏷️",
          porcentagem: totalDespesasVariaveis
            ? ((despesasVariaveis[categoria]?.valor / totalDespesasVariaveis) * 100).toFixed(2)
            : "0.00",
        }))
      );

      if (!isVisaoGeral && type === "Despesas") {
        setDespesasCartao(despesasCartaoTemp);
      }

      setResultadosAnuais({
        saldo: totalReceitas - (totalDespesasFixas + totalDespesasVariaveis),
        totalReceitas,
        totalDespesasFixas,
        totalDespesasVariaveis,
      });

      if (isVisaoGeral) {
        if (filterType === "Mensal") {
          const monthlyFormatted = monthly
            .map((data, index) => {
              const saldo = data.receitas - (data.despesasFixas + data.despesasVariaveis);
              const taxaSaldo = data.receitas > 0 ? ((saldo / data.receitas) * 100).toFixed(0) : 0;
              return {
                mes: [
                  "Janeiro",
                  "Fevereiro",
                  "Março",
                  "Abril",
                  "Maio",
                  "Junho",
                  "Julho",
                  "Agosto",
                  "Setembro",
                  "Outubro",
                  "Novembro",
                  "Dezembro",
                ][index],
                ano: new Date().getFullYear(),
                receitas: data.receitas,
                despesasFixas: data.despesasFixas,
                despesasVariaveis: data.despesasVariaveis,
                saldo,
                taxaSaldo,
              };
            })
            .filter((data) => data.receitas > 0 || data.despesasFixas > 0 || data.despesasVariaveis > 0);
          setMonthlyData(monthlyFormatted);
        } else {
          const yearlyFormatted = Object.keys(yearlyData).map((year) => {
            const data = yearlyData[year];
            const saldo = data.receitas - (data.despesasFixas + data.despesasVariaveis);
            const taxaSaldo = data.receitas > 0 ? ((saldo / data.receitas) * 100).toFixed(0) : 0;
            return {
              ano: year,
              receitas: data.receitas,
              despesasFixas: data.despesasFixas,
              despesasVariaveis: data.despesasVariaveis,
              saldo,
              taxaSaldo,
            };
          });
          setMonthlyData(yearlyFormatted);
        }
        setAnnualData({
          totalReceitas,
          totalDespesasFixas,
          totalDespesasVariaveis,
          saldo: totalReceitas - (totalDespesasFixas + totalDespesasVariaveis),
          taxaSaldo:
            totalReceitas > 0
              ? (((totalReceitas - (totalDespesasFixas + totalDespesasVariaveis)) / totalReceitas) * 100).toFixed(0)
              : 0,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados do Firestore:", error);
    }
  };

  const handleExportPDF = async (section = null) => {
    const doc = new jsPDF();

    const logoWidth = 50;
    const logoHeight = 30;
    const xPos = 14;
    const yPos = 5;
    doc.addImage(logoBranca, "PNG", xPos, yPos, logoWidth, logoHeight);

    doc.setFontSize(18);
    doc.setFont("Helvetica", "bold");
    let title =
      section === "VisaoGeral"
        ? "Relatório - Visão Geral"
        : section === "Despesas"
        ? "Relatório de Despesas"
        : "Extrato Detalhado";
    doc.text(title, 105, yPos + logoHeight + 10, { align: "center" });

    let currentY = yPos + logoHeight + 30;

    if (section === "VisaoGeral") {
      if (filterType === "Mensal") {
        doc.setFontSize(16);
        doc.setFont("Helvetica", "bold");
        doc.text("Resumo Mensal", 14, currentY);
        currentY += 10;

        const tableData = monthlyData.map((row) => [
          row.mes,
          formatCurrency(row.receitas, currency),
          formatCurrency(row.despesasFixas, currency),
          formatCurrency(row.despesasVariaveis, currency),
          formatCurrency(row.saldo, currency),
          `${row.taxaSaldo}%`,
        ]);

        doc.autoTable({
          startY: currentY,
          head: [["Mês", "Receitas", "Despesas Fixas", "Despesas Variáveis", "Saldo", "%"]],
          body: tableData,
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 123, 255], textColor: [255, 255, 255] },
        });
      } else {
        doc.setFontSize(16);
        doc.setFont("Helvetica", "bold");
        doc.text("Resumo Anual", 14, currentY);
        currentY += 10;

        const tableData = monthlyData.map((row) => [
          row.ano,
          formatCurrency(row.receitas, currency),
          formatCurrency(row.despesasFixas, currency),
          formatCurrency(row.despesasVariaveis, currency),
          formatCurrency(row.saldo, currency),
          `${row.taxaSaldo}%`,
        ]);

        doc.autoTable({
          startY: currentY,
          head: [["Ano", "Receitas", "Despesas Fixas", "Despesas Variáveis", "Saldo", "%"]],
          body: tableData,
          styles: { fontSize: 10 },
          headStyles: { fillColor: [0, 123, 255], textColor: [255, 255, 255] },
        });
      }
    } else if (section === "Despesas") {
      doc.setFontSize(16);
      doc.setFont("Helvetica", "bold");
      doc.text("Despesas Fixas", 14, currentY);
      currentY += 10;

      const tableDataFixas = despesasFixasData.map((item) => [
        item.categoria,
        formatCurrency(item.valor, currency),
        `${item.porcentagem}%`,
      ]);

      doc.autoTable({
        startY: currentY,
        head: [["Categoria", "Valor", "%"]],
        body: tableDataFixas,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [0, 123, 255], textColor: [255, 255, 255] },
      });

      currentY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(16);
      doc.setFont("Helvetica", "bold");
      doc.text("Despesas Variáveis", 14, currentY);
      currentY += 10;

      const tableDataVariaveis = despesasVariaveisData.map((item) => [
        item.categoria,
        formatCurrency(item.valor, currency),
        `${item.porcentagem}%`,
      ]);

      doc.autoTable({
        startY: currentY,
        head: [["Categoria", "Valor", "%"]],
        body: tableDataVariaveis,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [0, 123, 255], textColor: [255, 255, 255] },
      });

      currentY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(16);
      doc.setFont("Helvetica", "bold");
      doc.text("Gastos com Cartão de Crédito", 14, currentY);
      currentY += 10;

      const tableDataCartao = despesasCartao.map((item) => [item.categoria, formatCurrency(item.valor, currency)]);

      doc.autoTable({
        startY: currentY,
        head: [["Categoria", "Valor"]],
        body: tableDataCartao,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [0, 123, 255], textColor: [255, 255, 255] },
      });
    }

    doc.save(
      section === "VisaoGeral"
        ? "relatorio_visao_geral.pdf"
        : section === "Despesas"
        ? "relatorio_despesas.pdf"
        : "extrato_detalhado.pdf"
    );
  };

  const handleFetchData = () => {
    if (endDate && startDate && new Date(endDate) < new Date(startDate)) {
      alert("A data final deve ser superior à data inicial.");
      return;
    }
    fetchData(null, null, null, startDate, endDate);
  };

  const renderRelatorio = () => {
    switch (tabIndex) {
      case 0: {
        // --- prepara dados do gráfico minimalista (Receitas x Despesas empilhadas) ---
        const isMobile = typeof window !== "undefined" && window.innerWidth <= 600;
        const labels =
          filterType === "Mensal"
            ? monthlyData.map((r) => (isMobile ? abbrevMonthPt(r.mes) : r.mes))
            : monthlyData.map((r) => String(r.ano));
        const receitasVals = monthlyData.map((r) => r.receitas || 0);
        const fixasVals = monthlyData.map((r) => r.despesasFixas || 0);
        const variaveisVals = monthlyData.map((r) => r.despesasVariaveis || 0);

        const corReceitas = darkMode ? "#90caf9" : "#3f51b5";
        const corFixas = darkMode ? "#f44336" : "#e91e63";
        const corVar = darkMode ? "#ff9800" : "#ff5722";
        // saldo positivo em VERDE
        const corSaldoPos = darkMode ? "#66bb6a" : "#2e7d32";

        const barData = {
          labels,
          datasets: [
            {
              label: "Receitas",
              data: receitasVals,
              backgroundColor: corReceitas,
              borderRadius: 6,
              borderSkipped: false,
              barPercentage: 0.55,
              categoryPercentage: 0.6,
              stack: "Receitas",
              order: 1,
            },
            {
              label: "Despesas Fixas",
              data: fixasVals,
              backgroundColor: corFixas,
              borderRadius: 6,
              borderSkipped: false,
              barPercentage: 0.55,
              categoryPercentage: 0.6,
              stack: "Despesas", // empilha com variáveis
              order: 2,
            },
            {
              label: "Despesas Variáveis",
              data: variaveisVals,
              backgroundColor: corVar,
              borderRadius: 6,
              borderSkipped: false,
              barPercentage: 0.55,
              categoryPercentage: 0.6,
              stack: "Despesas", // empilha com fixas
              order: 3,
            },
          ],
        };

        const barOptions = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
              align: "center", // 🔽 legenda centralizada
              labels: {
                color: darkMode ? "#fff" : "#333",
                boxWidth: 8,
                boxHeight: 8,
                usePointStyle: true,
                font: {
                  size: isMobile ? 10 : 12,
                },
                padding: 20, // 🔽 espaço extra entre legenda e gráfico
              },
            },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label || ""}: ${formatCurrency(ctx.parsed.y || 0, currency)}`,
                afterBody: (items) => {
                  if (!items || !items.length) return "";
                  const i = items[0].dataIndex;
                  const totalDesp = (fixasVals[i] || 0) + (variaveisVals[i] || 0);
                  return totalDesp > 0 ? `Total Despesas: ${formatCurrency(totalDesp, currency)}` : "";
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false, drawBorder: false },
              ticks: { color: darkMode ? "#bbb" : "#666" },
            },
            y: {
              stacked: true,
              grid: { display: false, drawBorder: false },
              ticks: {
                color: darkMode ? "#bbb" : "#666",
                // Em mobile abrevia (1k/1M); em telas maiores mantém moeda completa
                callback: (val) =>
                  typeof window !== "undefined" && window.innerWidth <= 600
                    ? abbreviateNumber(val)
                    : formatCurrency(val, currency),
              },
            },
          },
        };
        return (
          <Card sx={{ backgroundColor: darkMode ? "#333" : "#fff" }}>
            <MDBox p={3}>
              {/* Cabeçalho com título, filtro e ícone de exportar */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                  Visão Geral
                </MDTypography>

                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
                    <Select
                      value={filterType}
                      onChange={handleFilterChange}
                      sx={{
                        height: 34,
                        borderRadius: "10px",
                        backgroundColor: darkMode ? "#444" : "#f7f7f7",
                        color: darkMode ? "#fff" : "#333",
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      }}
                    >
                      <MenuItem value="Mensal">Mensal</MenuItem>
                      <MenuItem value="Anual">Anual</MenuItem>
                    </Select>
                  </FormControl>

                  <Tooltip title="Exportar PDF">
                    <IconButton
                      onClick={() => handleExportPDF("VisaoGeral")}
                      sx={{
                        height: 34,
                        width: 40,
                        borderRadius: "10px",
                        backgroundColor: darkMode ? "#444" : "#fff",
                        color: darkMode ? "#fff" : "#1976d2",
                        border: darkMode ? "1px solid #666" : "1px solid #ddd",
                        "&:hover": { backgroundColor: darkMode ? "#555" : "#f0f0f0" },
                      }}
                      aria-label="Exportar"
                    >
                      <DownloadRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Linha de cima: Resumo (esq) + Gráfico (dir) */}
              <Grid container spacing={2} alignItems="stretch">
                {/* Esquerda: um único card com 4 linhas de resumo */}
                <Grid item xs={12} md={5}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      height: "100%",
                      backgroundColor: darkMode ? "#444" : "#fff",
                      border: darkMode ? "1px solid #666" : "1px solid #eee",
                    }}
                  >
                    {[
                      {
                        label: "Saldo atual",
                        value: formatCurrency(annualData?.saldo || 0, currency),
                        // verde quando positivo, vermelho quando negativo
                        color: (annualData?.saldo ?? 0) >= 0 ? corSaldoPos : darkMode ? "#f44336" : "#e91e63",
                      },
                      {
                        label: "Receitas",
                        value: formatCurrency(annualData?.totalReceitas || 0, currency),
                        color: corReceitas,
                      },
                      {
                        label: "Despesas Fixas",
                        value: formatCurrency(annualData?.totalDespesasFixas || 0, currency),
                        color: corFixas,
                      },
                      {
                        label: "Despesas Variáveis",
                        value: formatCurrency(annualData?.totalDespesasVariaveis || 0, currency),
                        color: corVar,
                      },
                    ].map((c, i) => (
                      <MDBox
                        key={i}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        p={1.5}
                        mb={i !== 3 ? 1.25 : 0}
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: darkMode ? "#3d3d3d" : "#fafafa",
                          border: darkMode ? "1px solid #555" : "1px solid #eee",
                        }}
                      >
                        <MDTypography variant="body2" fontWeight="bold" color={darkMode ? "#ddd" : "#555"}>
                          {c.label}
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: c.color }}>
                          {c.value}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </Card>
                </Grid>

                {/* Direita: gráfico minimalista */}
                <Grid item xs={12} md={7}>
                  <Card
                    sx={{
                      p: { xs: 2, md: 3 },
                      borderRadius: "12px",
                      height: "100%",
                      backgroundColor: darkMode ? "#3a3a3a" : "#fff",
                      border: darkMode ? "1px solid #666" : "1px solid #eee",
                    }}
                  >
                    <MDTypography variant="h6" fontWeight="bold" mb={1} color={darkMode ? "white" : "dark"}>
                      {filterType === "Mensal" ? "Receitas x Despesas (Mensal)" : "Receitas x Despesas (Anual)"}
                    </MDTypography>

                    <MDBox sx={{ position: "relative", width: "100%", height: { xs: 300, md: 380 } }}>
                      <Bar data={barData} options={barOptions} />
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>

              {/* Card de baixo: tabela (respeita o mesmo filtro) */}
              <MDBox mt={2}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    backgroundColor: darkMode ? "#444" : "#fff",
                    border: darkMode ? "1px solid #666" : "1px solid #eee",
                  }}
                >
                  {filterType === "Mensal" ? (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={1} color={darkMode ? "white" : "dark"}>
                        Resumo Mensal
                      </MDTypography>
                      <MDBox sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                          <thead>
                            <tr>
                              <th style={{ padding: "10px", textAlign: "left", color: darkMode ? "#bbb" : "#555" }}>
                                Mês
                              </th>
                              <th style={{ padding: "10px", textAlign: "left", color: corReceitas }}>Receitas</th>
                              <th style={{ padding: "10px", textAlign: "left", color: corFixas }}>Despesas Fixas</th>
                              <th style={{ padding: "10px", textAlign: "left", color: corVar }}>Despesas Variáveis</th>
                              <th style={{ padding: "10px", textAlign: "left", color: darkMode ? "#bbb" : "#555" }}>
                                Saldo
                              </th>
                              <th style={{ padding: "10px", textAlign: "left", color: darkMode ? "#bbb" : "#555" }}>
                                %
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthlyData.map((row, index) => (
                              <tr key={index} style={{ borderTop: `1px solid ${darkMode ? "#555" : "#eee"}` }}>
                                <td style={{ padding: "10px", color: darkMode ? "#eee" : "#333" }}>{row.mes}</td>
                                <td style={{ padding: "10px", color: corReceitas }}>
                                  {formatCurrency(row.receitas, currency)}
                                </td>
                                <td style={{ padding: "10px", color: corFixas }}>
                                  {formatCurrency(row.despesasFixas, currency)}
                                </td>
                                <td style={{ padding: "10px", color: corVar }}>
                                  {formatCurrency(row.despesasVariaveis, currency)}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    color: (row.saldo || 0) >= 0 ? corSaldoPos : darkMode ? "#f44336" : "#e91e63",
                                  }}
                                >
                                  {formatCurrency(row.saldo, currency)}
                                </td>
                                <td style={{ padding: "10px" }}>
                                  <span
                                    style={{
                                      backgroundColor: darkMode ? "#4a6a7d" : "#e0f7fa",
                                      color: darkMode ? "#a3e4ff" : "#26c6da",
                                      padding: "2px 10px",
                                      borderRadius: "10px",
                                      fontSize: "0.85rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {row.taxaSaldo}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={1} color={darkMode ? "white" : "dark"}>
                        Resumo Anual
                      </MDTypography>
                      <MDBox sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                          <thead>
                            <tr>
                              <th style={{ padding: "10px", textAlign: "left", color: darkMode ? "#bbb" : "#555" }}>
                                Ano
                              </th>
                              <th style={{ padding: "10px", textAlign: "left", color: corReceitas }}>Receitas</th>
                              <th style={{ padding: "10px", textAlign: "left", color: corFixas }}>Despesas Fixas</th>
                              <th style={{ padding: "10px", textAlign: "left", color: corVar }}>Despesas Variáveis</th>
                              <th style={{ padding: "10px", textAlign: "left", color: darkMode ? "#bbb" : "#555" }}>
                                Saldo
                              </th>
                              <th style={{ padding: "10px", textAlign: "left", color: darkMode ? "#bbb" : "#555" }}>
                                %
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthlyData.map((row, index) => (
                              <tr key={index} style={{ borderTop: `1px solid ${darkMode ? "#555" : "#eee"}` }}>
                                <td style={{ padding: "10px", color: darkMode ? "#eee" : "#333" }}>{row.ano}</td>
                                <td style={{ padding: "10px", color: corReceitas }}>
                                  {formatCurrency(row.receitas, currency)}
                                </td>
                                <td style={{ padding: "10px", color: corFixas }}>
                                  {formatCurrency(row.despesasFixas, currency)}
                                </td>
                                <td style={{ padding: "10px", color: corVar }}>
                                  {formatCurrency(row.despesasVariaveis, currency)}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    color: (row.saldo || 0) >= 0 ? corSaldoPos : darkMode ? "#f44336" : "#e91e63",
                                  }}
                                >
                                  {formatCurrency(row.saldo, currency)}
                                </td>
                                <td style={{ padding: "10px" }}>
                                  <span
                                    style={{
                                      backgroundColor: darkMode ? "#4a6a7d" : "#e0f7fa",
                                      color: darkMode ? "#a3e4ff" : "#26c6da",
                                      padding: "2px 10px",
                                      borderRadius: "10px",
                                      fontSize: "0.85rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {row.taxaSaldo}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </MDBox>
                    </MDBox>
                  )}
                </Card>
              </MDBox>
            </MDBox>
          </Card>
        );
      }
      case 1: {
        const titleReceitas = "Relatório de Receitas";
        const monthNames = [
          "Janeiro",
          "Fevereiro",
          "Março",
          "Abril",
          "Maio",
          "Junho",
          "Julho",
          "Agosto",
          "Setembro",
          "Outubro",
          "Novembro",
          "Dezembro",
        ];

        const totalReceitasMonth = receitasData.reduce((acc, item) => acc + item.valor, 0);
        const numReceitas = receitasData.length;

        // --- Paleta azul (cicla se houver mais categorias que cores) ---
        const bluePalette = [
          "#E3F2FD",
          "#BBDEFB",
          "#90CAF9",
          "#64B5F6",
          "#42A5F5",
          "#2196F3",
          "#1E88E5",
          "#1976D2",
          "#1565C0",
          "#0D47A1",
        ];
        const receitasColors = Array.from({ length: numReceitas }, (_, i) => bluePalette[i % bluePalette.length]);

        // cor do fundo do card (para "abrir" espaço entre segmentos)
        const cardBg = darkMode ? "#333" : "#fff";

        return (
          <Card sx={{ backgroundColor: darkMode ? "#333" : "#fff" }}>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="center" alignItems="center" mb={2}>
                <Button
                  onClick={() => handleMonthChange(1, -1)}
                  sx={{
                    minWidth: "24px",
                    height: "24px",
                    color: darkMode ? "#fff" : "#333",
                    "&:hover": { backgroundColor: "transparent" },
                    "&:before": { content: '"◄"', fontSize: "16px", display: "inline-block" },
                    "&:after": { content: "none" },
                  }}
                />
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    mx: 2,
                    textAlign: "center",
                    minWidth: "120px",
                    color: darkMode ? "#90caf9" : "#1976d2",
                  }}
                >
                  {`${monthNames[receitasMonth - 1]} ${receitasYear}`}
                </MDTypography>
                <Button
                  onClick={() => handleMonthChange(1, 1)}
                  sx={{
                    minWidth: "24px",
                    height: "24px",
                    color: darkMode ? "#fff" : "#333",
                    "&:hover": { backgroundColor: "transparent" },
                    "&:before": { content: '"►"', fontSize: "16px", display: "inline-block" },
                    "&:after": { content: "none" },
                  }}
                />
              </MDBox>

              <MDTypography variant="h5" color={darkMode ? "white" : "dark"}>
                {titleReceitas}
              </MDTypography>

              <MDBox mt={2}>
                <Doughnut
                  data={{
                    labels: receitasData.map((item) => item.categoria),
                    datasets: [
                      {
                        data: receitasData.map((item) => item.valor || 0),
                        backgroundColor: receitasColors,
                        // borda mais grossa + cor do card para dar o "gap" entre fatias
                        borderWidth: 6,
                        borderColor: cardBg,
                        hoverOffset: 6,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: "70%",
                    plugins: {
                      legend: {
                        display: window.innerWidth >= 600,
                        position: "top",
                        labels: { color: darkMode ? "#fff" : "#333" },
                      },
                      tooltip: {
                        callbacks: {
                          label: (t) => `${t.label}: ${formatCurrency(t.raw, currency)}`,
                        },
                      },
                      doughnutLabel: {
                        color: darkMode ? "#1976D2" : "#1565C0",
                      },
                    },
                  }}
                  style={{
                    maxWidth: "100%",
                    maxHeight: window.innerWidth < 600 ? "240px" : "300px",
                    transform: window.innerWidth < 600 ? "scale(0.8)" : "none",
                  }}
                />

                <MDBox mt={2}>
                  <MDTypography variant="h6" mb={2} color={darkMode ? "white" : "dark"}>
                    Categorias - Detalhamento
                  </MDTypography>
                  <ul
                    style={{
                      listStyleType: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {receitasData.map((item, index) => (
                      <li
                        key={index}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "16px",
                          backgroundColor: darkMode ? "#444" : "#fff",
                          borderRadius: "12px",
                          boxShadow: darkMode ? "0 2px 4px rgba(255, 255, 255, 0.1)" : "0 2px 4px rgba(0, 0, 0, 0.1)",
                          border: darkMode ? "1px solid #666" : "1px solid #e0e0e0",
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                          <span style={{ fontSize: "1.8rem" }}>{item.emoji}</span>
                          <span
                            style={{
                              fontSize: "1rem",
                              fontWeight: "bold",
                              color: darkMode ? "#fff" : "#333",
                              wordWrap: "break-word",
                            }}
                          >
                            {item.categoria}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: window.innerWidth < 600 ? "column" : "row",
                            alignItems: window.innerWidth < 600 ? "flex-end" : "center",
                            gap: window.innerWidth < 600 ? "0px" : "16px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "1rem",
                              fontWeight: "bold",
                              color: darkMode ? "#90caf9" : "#3f51b5",
                            }}
                          >
                            {formatCurrency(item.valor, currency)}
                          </span>
                          <span style={{ fontSize: "1rem", fontWeight: "bold", color: "#8a2be2" }}>
                            {item.porcentagem}%
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        );
      }
      case 2:
        const titleDespesas = "Relatório de Despesas";
        const monthNamesDespesas = [
          "Janeiro",
          "Fevereiro",
          "Março",
          "Abril",
          "Maio",
          "Junho",
          "Julho",
          "Agosto",
          "Setembro",
          "Outubro",
          "Novembro",
          "Dezembro",
        ];
        const totalDespesasFixasMonth = despesasFixasData.reduce((acc, item) => acc + item.valor, 0);
        const totalDespesasVariaveisMonth = despesasVariaveisData.reduce((acc, item) => acc + item.valor, 0);

        const numFixas = despesasFixasData.length;
        const numVariaveis = despesasVariaveisData.length;

        // Paletas específicas
        const redPalette = [
          "#FFEBEE",
          "#FFCDD2",
          "#EF9A9A",
          "#E57373",
          "#EF5350",
          "#F44336",
          "#E53935",
          "#D32F2F",
          "#C62828",
          "#B71C1C",
        ];
        const orangePalette = [
          "#FFF3E0",
          "#FFE0B2",
          "#FFCC80",
          "#FFB74D",
          "#FFA726",
          "#FF9800",
          "#FB8C00",
          "#F57C00",
          "#EF6C00",
          "#E65100",
        ];

        const fixasColors = Array.from({ length: numFixas }, (_, i) => redPalette[i % redPalette.length]);
        const variaveisColors = Array.from({ length: numVariaveis }, (_, i) => orangePalette[i % orangePalette.length]);

        return (
          <Card sx={{ backgroundColor: darkMode ? "#333" : "#fff" }}>
            <MDBox p={3}>
              {/* Navegação de mês */}
              <MDBox display="flex" justifyContent="center" alignItems="center" mb={2}>
                <Button
                  onClick={() => handleMonthChange(2, -1)}
                  sx={{
                    minWidth: 24,
                    height: 24,
                    color: darkMode ? "#fff" : "#333",
                    "&:hover": { backgroundColor: "transparent" },
                    "&:before": { content: '"◄"', fontSize: 16, display: "inline-block" },
                    "&:after": { content: "none" },
                  }}
                />
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mx: 2, textAlign: "center", minWidth: 120, color: darkMode ? "#90caf9" : "#1976d2" }}
                >
                  {`${monthNamesDespesas[despesasMonth - 1]} ${despesasYear}`}
                </MDTypography>
                <Button
                  onClick={() => handleMonthChange(2, 1)}
                  sx={{
                    minWidth: 24,
                    height: 24,
                    color: darkMode ? "#fff" : "#333",
                    "&:hover": { backgroundColor: "transparent" },
                    "&:before": { content: '"►"', fontSize: 16, display: "inline-block" },
                    "&:after": { content: "none" },
                  }}
                />
              </MDBox>

              <MDTypography variant="h5" color={darkMode ? "white" : "dark"}>
                {titleDespesas}
              </MDTypography>

              {/* ====== GRID: Fixas (esq) + Variáveis (dir) ====== */}
              <Grid container spacing={2} mt={1}>
                {/* Card: Despesas Fixas */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor: darkMode ? "#444" : "#fff",
                      border: darkMode ? "1px solid #666" : "1px solid #e0e0e0",
                      height: "100%",
                    }}
                  >
                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
                      Despesas Fixas
                    </MDTypography>

                    <MDBox mt={1}>
                      <Doughnut
                        data={{
                          labels: despesasFixasData.map((i) => i.categoria),
                          datasets: [
                            {
                              data: despesasFixasData.map((i) => i.valor || 0),
                              backgroundColor: fixasColors,
                              borderWidth: 2,
                              borderColor: darkMode ? "#666" : "#e0e0e0",
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          cutout: "70%",
                          plugins: {
                            legend: {
                              display: window.innerWidth >= 600,
                              position: "top",
                              labels: { color: darkMode ? "#fff" : "#333" },
                            },
                            tooltip: {
                              callbacks: {
                                label: (t) => `${t.label}: ${formatCurrency(t.raw, currency)}`,
                              },
                            },
                            doughnutLabel: { color: darkMode ? "#f44336" : "#e91e63" },
                          },
                        }}
                        style={{
                          maxWidth: "100%",
                          maxHeight: window.innerWidth < 600 ? "240px" : "300px",
                          transform: window.innerWidth < 600 ? "scale(0.8)" : "none",
                        }}
                      />
                    </MDBox>

                    <MDBox mt={2}>
                      <ul
                        style={{
                          listStyleType: "none",
                          padding: 0,
                          margin: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                        }}
                      >
                        {despesasFixasData.map((item, index) => (
                          <li
                            key={index}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: 16,
                              backgroundColor: darkMode ? "#3f3f3f" : "#fff",
                              borderRadius: 12,
                              boxShadow: darkMode ? "0 2px 4px rgba(255,255,255,0.08)" : "0 2px 4px rgba(0,0,0,0.06)",
                              border: darkMode ? "1px solid #5e5e5e" : "1px solid #e0e0e0",
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                              <span style={{ fontSize: "1.8rem" }}>{item.emoji}</span>
                              <span style={{ fontSize: "1rem", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                {item.categoria}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: window.innerWidth < 600 ? "column" : "row",
                                alignItems: window.innerWidth < 600 ? "flex-end" : "center",
                                gap: window.innerWidth < 600 ? 0 : 16,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "1rem",
                                  fontWeight: "bold",
                                  color: darkMode ? "#f44336" : "#e91e63",
                                }}
                              >
                                {formatCurrency(item.valor, currency)}
                              </span>
                              <span style={{ fontSize: "1rem", fontWeight: "bold", color: "#8a2be2" }}>
                                {item.porcentagem}%
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </MDBox>
                  </Card>
                </Grid>

                {/* Card: Despesas Variáveis */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor: darkMode ? "#444" : "#fff",
                      border: darkMode ? "1px solid #666" : "1px solid #e0e0e0",
                      height: "100%",
                    }}
                  >
                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
                      Despesas Variáveis
                    </MDTypography>

                    <MDBox mt={1}>
                      <Doughnut
                        data={{
                          labels: despesasVariaveisData.map((i) => i.categoria),
                          datasets: [
                            {
                              data: despesasVariaveisData.map((i) => i.valor || 0),
                              backgroundColor: variaveisColors,
                              borderWidth: 2,
                              borderColor: darkMode ? "#666" : "#e0e0e0",
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          cutout: "70%",
                          plugins: {
                            legend: {
                              display: window.innerWidth >= 600,
                              position: "top",
                              labels: { color: darkMode ? "#fff" : "#333" },
                            },
                            tooltip: {
                              callbacks: {
                                label: (t) => `${t.label}: ${formatCurrency(t.raw, currency)}`,
                              },
                            },
                            doughnutLabel: { color: darkMode ? "#ff9800" : "#ff5722" },
                          },
                        }}
                        style={{
                          maxWidth: "100%",
                          maxHeight: window.innerWidth < 600 ? "240px" : "300px",
                          transform: window.innerWidth < 600 ? "scale(0.8)" : "none",
                        }}
                      />
                    </MDBox>

                    <MDBox mt={2}>
                      <ul
                        style={{
                          listStyleType: "none",
                          padding: 0,
                          margin: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                        }}
                      >
                        {despesasVariaveisData.map((item, index) => (
                          <li
                            key={index}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: 16,
                              backgroundColor: darkMode ? "#3f3f3f" : "#fff",
                              borderRadius: 12,
                              boxShadow: darkMode ? "0 2px 4px rgba(255,255,255,0.08)" : "0 2px 4px rgba(0,0,0,0.06)",
                              border: darkMode ? "1px solid #5e5e5e" : "1px solid #e0e0e0",
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                              <span style={{ fontSize: "1.8rem" }}>{item.emoji}</span>
                              <span style={{ fontSize: "1rem", fontWeight: "bold", color: darkMode ? "#fff" : "#333" }}>
                                {item.categoria}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: window.innerWidth < 600 ? "column" : "row",
                                alignItems: window.innerWidth < 600 ? "flex-end" : "center",
                                gap: window.innerWidth < 600 ? 0 : 16,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "1rem",
                                  fontWeight: "bold",
                                  color: darkMode ? "#ff9800" : "#ff5722",
                                }}
                              >
                                {formatCurrency(item.valor, currency)}
                              </span>
                              <span style={{ fontSize: "1rem", fontWeight: "bold", color: "#8a2be2" }}>
                                {item.porcentagem}%
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        );
      default:
        return null;
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{
            p: 0.5,
            mx: "auto",
            width: { xs: "100%", sm: "auto" },
            borderRadius: 9999,
            background: darkMode ? "rgba(17,25,40,0.55)" : "rgba(255,255,255,0.65)",
            border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(30,136,229,0.15)",
            boxShadow: darkMode ? "0 6px 18px rgba(0,0,0,0.35)" : "0 6px 18px rgba(30,136,229,0.08)",
            backdropFilter: "saturate(180%) blur(8px)",
            "& .MuiTabs-indicator": { display: "none" },

            "& .MuiTab-root": {
              minHeight: 40,
              borderRadius: 9999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: "0.9rem", md: "1rem" },
              px: { xs: 1.5, md: 2.5 },
              transition: "all .2s ease",
              color: darkMode ? "rgba(255,255,255,0.72)" : "#5E7FA3",

              "&:hover": {
                backgroundColor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(221,241,255,0.7)",
              },

              "&.Mui-selected": {
                color: darkMode ? "#A6D9FF" : "#1565D8",
                background: darkMode
                  ? "linear-gradient(180deg, rgba(42,56,76,0.9) 0%, rgba(26,33,46,0.9) 100%)"
                  : "linear-gradient(180deg, #E6F5FF 0%, #F4FAFF 100%)",
                border: darkMode ? "1px solid rgba(166,217,255,0.35)" : "1px solid #BFE7FF",
                boxShadow: darkMode
                  ? "0 6px 14px rgba(0,0,0,0.40), inset 0 -2px 0 rgba(166,217,255,0.12)"
                  : "0 6px 14px rgba(21,101,216,0.12), inset 0 -2px 0 rgba(21,101,216,0.06)",
              },
            },
          }}
        >
          <Tab disableRipple label="Visão Geral" />
          <Tab disableRipple label="Receitas" />
          <Tab disableRipple label="Despesas" />
        </Tabs>
        <Divider />
        <MDBox mt={3}>{renderRelatorio()}</MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default Relatorios;
